import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { logApiError } from "../utils/apiLogger";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// base64url-safe decode
const base64UrlToBase64 = (str) => str.replace(/-/g, "+").replace(/_/g, "/");

const parseJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = base64UrlToBase64(payload);
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const normalizeRole = (raw) => {
  if (!raw) return null;

  let r = raw;

  // handle arrays or objects
  if (Array.isArray(r)) r = r[0];
  if (typeof r === "object" && r?.authority) r = r.authority;

  r = String(r);
  if (r.startsWith("ROLE_")) r = r.replace("ROLE_", "");
  return r.toLowerCase(); // "client_manager"
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState(null);
  const [name, setName] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const logoutTimer = useRef(null);
  const rememberMeRef = useRef(false);
  const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setRole(null);
    setUsername(null);
    setName(null);
    setMustChangePassword(false);
    sessionStorage.clear();
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    localStorage.removeItem("name");
    localStorage.removeItem("mustChangePassword");
    localStorage.removeItem("rememberMe");
    clearTimeout(logoutTimer.current);
  }, []);

  const updateSessionUser = useCallback(({ token, username: newUname, name: newName, role: newRole, mustChangePassword: newMustChange }) => {
    if (token) {
      sessionStorage.setItem("jwtToken", token);
      if (rememberMeRef.current) {
        localStorage.setItem("jwtToken", token);
      }
    }
    if (newUname) {
      setUsername(newUname);
      sessionStorage.setItem("username", newUname);
      if (rememberMeRef.current) localStorage.setItem("username", newUname);
    }
    if (newName) {
      setName(newName);
      sessionStorage.setItem("name", newName);
      if (rememberMeRef.current) localStorage.setItem("name", newName);
    }
    if (newRole) {
      const nr = normalizeRole(newRole);
      setRole(nr);
      sessionStorage.setItem("role", nr);
      if (rememberMeRef.current) localStorage.setItem("role", nr);
    }
    if (newMustChange !== undefined) {
      setMustChangePassword(!!newMustChange);
      sessionStorage.setItem("mustChangePassword", newMustChange ? "true" : "false");
      if (rememberMeRef.current) localStorage.setItem("mustChangePassword", newMustChange ? "true" : "false");
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(logoutTimer.current);
    logoutTimer.current = setTimeout(logout, INACTIVITY_LIMIT);
  }, [logout]);

  const startInactivityTracking = useCallback(() => {
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetInactivityTimer));
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const stopInactivityTracking = useCallback(() => {
    clearTimeout(logoutTimer.current);
    const events = ["mousemove", "keydown", "mousedown", "scroll", "touchstart"];
    events.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
  }, [resetInactivityTimer]);

  useEffect(() => {
    // Check sessionStorage first, then localStorage (Remember Me)
    let token = sessionStorage.getItem("jwtToken");
    let storedRole = normalizeRole(sessionStorage.getItem("role"));
    let storedUsername = sessionStorage.getItem("username");
    let storedName = sessionStorage.getItem("name");
    let storedMustChange = sessionStorage.getItem("mustChangePassword") === "true";
    let isRemembered = false;

    if (!token && localStorage.getItem("rememberMe") === "true") {
      token = localStorage.getItem("jwtToken");
      storedRole = normalizeRole(localStorage.getItem("role"));
      storedUsername = localStorage.getItem("username");
      storedName = localStorage.getItem("name");
      storedMustChange = localStorage.getItem("mustChangePassword") === "true";
      isRemembered = true;
    }

    if (token) {
      const payload = parseJwt(token);

      // Check if token is expired
      if (payload?.exp && payload.exp * 1000 < Date.now()) {
        // Token expired, clear everything
        sessionStorage.clear();
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("role");
        localStorage.removeItem("username");
        localStorage.removeItem("name");
        localStorage.removeItem("mustChangePassword");
        localStorage.removeItem("rememberMe");
        return stopInactivityTracking;
      }

      const roleFromToken = normalizeRole(
        payload?.role ?? payload?.roles ?? payload?.authorities ?? payload?.authority
      );

      const finalRole = roleFromToken || storedRole;
      const isTemp = !!(payload?.tempPassword || payload?.mustChangePassword || storedMustChange);

      if (finalRole && (payload?.sub || storedUsername)) {
        setIsAuthenticated(true);
        setRole(finalRole);
        setUsername(payload?.sub || storedUsername);
        setName(payload?.name || storedName);
        setMustChangePassword(isTemp);
        rememberMeRef.current = isRemembered;

        // If remembered, also populate sessionStorage for API calls
        if (isRemembered) {
          sessionStorage.setItem("jwtToken", token);
          sessionStorage.setItem("role", finalRole);
          sessionStorage.setItem("username", payload?.sub || storedUsername);
          sessionStorage.setItem("name", payload?.name || storedName);
          sessionStorage.setItem("mustChangePassword", isTemp ? "true" : "false");
        }

        if (!isRemembered) {
          startInactivityTracking();
        }
      }
    } else if (storedRole && storedUsername) {
      setIsAuthenticated(true);
      setRole(storedRole);
      setUsername(storedUsername);
      setName(storedName);
      setMustChangePassword(storedMustChange);
      startInactivityTracking();
    }

    return stopInactivityTracking;
  }, [startInactivityTracking, stopInactivityTracking]);

  const login = async (username, password, rememberMe = false) => {
    const correlationId = uuidv4();

    try {
      const response = await fetch("/timesheets/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Correlation-Id": correlationId,
        },
        body: JSON.stringify({ username, password, rememberMe }),
      });

      if (!response.ok) {
        logApiError(new Error(`Login failed with status ${response.status}`), {
          userId: username,
          correlationId,
        });
        if (response.status === 403) {
          const body = await response.json().catch(() => ({}));
          return { success: false, message: body.error || 'Your account is inactive. Please contact the admin.' };
        }
        return { success: false, message: 'Invalid username or password. Please try again.' };
      }

      const token = await response.text();
      const payload = parseJwt(token);

      const roleFromToken = normalizeRole(
        payload?.role ?? payload?.roles ?? payload?.authorities ?? payload?.authority
      );

      if (roleFromToken && payload?.sub) {
        const isTemp = !!(payload?.tempPassword || payload?.mustChangePassword);
        setIsAuthenticated(true);
        setRole(roleFromToken);
        setUsername(payload.sub);
        setName(payload.name);
        setMustChangePassword(isTemp);
        rememberMeRef.current = rememberMe;

        // Always store in sessionStorage for current session API calls
        sessionStorage.setItem("jwtToken", token);
        sessionStorage.setItem("role", roleFromToken);
        sessionStorage.setItem("username", payload.sub);
        sessionStorage.setItem("name", payload.name);
        sessionStorage.setItem("mustChangePassword", isTemp ? "true" : "false");

        // If Remember Me, also persist in localStorage for cross-session persistence
        if (rememberMe) {
          localStorage.setItem("jwtToken", token);
          localStorage.setItem("role", roleFromToken);
          localStorage.setItem("username", payload.sub);
          localStorage.setItem("name", payload.name);
          localStorage.setItem("mustChangePassword", isTemp ? "true" : "false");
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("role");
          localStorage.removeItem("username");
          localStorage.removeItem("name");
          localStorage.removeItem("mustChangePassword");
          localStorage.removeItem("rememberMe");
        }

        if (!rememberMe) {
          startInactivityTracking();
        }
        return { success: true };
      }

      logApiError(new Error("Invalid token payload"), {
        userId: username,
        correlationId,
      });
      return { success: false, message: 'Invalid username or password. Please try again.' };
    } catch (err) {
      logApiError(err, { userId: username, correlationId });
      return { success: false, message: 'Invalid username or password. Please try again.' };
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, role, username, name, mustChangePassword, setMustChangePassword, updateSessionUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
